import type { RecordExport } from '@wodi/shared';
import {
  PrintFooter,
  PrintHeader,
  PrintLines,
  PrintSheet,
  PrintTable,
  type PrintLine,
} from '@wodi/ui';
import type { Ref } from 'react';

import { formatInstant, formatIsoDate } from '../patients/format.ts';
import styles from './records.module.css';

// The printed summary of a patient's record for an access request
// (docs/plans/phase2-records-data-rights.md §5, §6): the same content as the file, on A4,
// black on white, flowing onto as many pages as it needs. "Save as PDF" in the print dialog
// gives the PDF copy.

const VISIT_TYPE_WORDS: Record<string, string> = {
  outpatient: 'Outpatient',
  walk_in: 'Walk-in',
  inpatient: 'Inpatient stay',
  emergency: 'Casualty',
  counter_sale: 'Pharmacy counter',
  mortuary: 'Mortuary',
};

const IDENTIFIER_WORDS: Record<string, string> = {
  cr_id: 'CR ID',
  national_id: 'National ID',
  birth_certificate: 'Birth certificate',
  passport: 'Passport',
  alien_id: 'Alien ID',
  refugee_id: 'Refugee ID',
  legacy_patient_number: 'Earlier patient number',
};

function allergyText(allergies: RecordExport['record']['allergies']): string {
  if (allergies.state === 'no_known_allergies') return 'No known allergies';
  if (allergies.state === 'not_recorded') return 'Allergies not recorded';
  return allergies.allergies
    .map((a) => (a.reaction === null ? a.substance : `${a.substance} (${a.reaction})`))
    .join('; ');
}

export function RecordSummarySheet({
  data,
  requestNumber,
  ref,
}: {
  data: RecordExport;
  requestNumber: string;
  ref?: Ref<HTMLElement>;
}) {
  const r = data.record;
  const lines: PrintLine[] = [
    { label: 'Name', value: r.patient.displayName },
    { label: 'Patient no.', value: r.patient.patientNumber, identifier: true },
    { label: 'Sex', value: r.patient.sex },
    {
      label: 'Date of birth',
      value:
        r.patient.birthDate === null
          ? 'Not recorded'
          : `${formatIsoDate(r.patient.birthDate)}${r.patient.birthDateEstimated ? ' (estimated)' : ''}`,
    },
    ...r.patient.identifiers.map((i) => ({
      label: IDENTIFIER_WORDS[i.system] ?? i.system,
      value: i.value,
      identifier: true,
    })),
    ...(r.patient.phones.length === 0
      ? []
      : [{ label: 'Phone', value: r.patient.phones.join(', ') }]),
    ...(r.patient.residence === null ? [] : [{ label: 'Residence', value: r.patient.residence }]),
    { label: 'Allergies', value: allergyText(r.allergies) },
    { label: 'Request', value: requestNumber, identifier: true },
    { label: 'Prepared', value: formatInstant(data.generatedAt) },
  ];
  return (
    <PrintSheet paper="a4" label="Patient record summary" {...(ref === undefined ? {} : { ref })}>
      <PrintHeader facility={data.facility.name} title="Patient record summary" />
      <PrintLines items={lines} />
      <section className={styles.printSection}>
        <h2>Problems</h2>
        {r.problems.length === 0 ? (
          <p>None recorded.</p>
        ) : (
          <PrintTable>
            <thead>
              <tr>
                <th>Problem</th>
                <th>Code</th>
                <th>Status</th>
                <th>Since</th>
              </tr>
            </thead>
            <tbody>
              {r.problems.map((p, i) => (
                <tr key={`${p.display}-${String(i)}`}>
                  <td>{p.display}</td>
                  <td>{p.code ?? ''}</td>
                  <td>{p.clinicalStatus}</td>
                  <td>{p.onsetDate === null ? '' : formatIsoDate(p.onsetDate)}</td>
                </tr>
              ))}
            </tbody>
          </PrintTable>
        )}
      </section>
      <section className={styles.printSection}>
        <h2>Visits</h2>
        {r.visits.length === 0 ? <p>No visits.</p> : null}
        {r.visits.map((v) => (
          <div key={v.id}>
            <h3>
              {formatInstant(v.startedAt)}, {VISIT_TYPE_WORDS[v.type] ?? v.type}, {v.facility}{' '}
              <span className="wodi-id">{v.visitNumber}</span>
              {v.status === 'cancelled' ? ' (cancelled)' : ''}
            </h3>
            {v.diagnoses.length === 0 ? null : (
              <p>
                Diagnoses:{' '}
                {v.diagnoses
                  .map((d) => (d.code === null ? d.display : `${d.display} (${d.code})`))
                  .join('; ')}
              </p>
            )}
            {v.vitals.length === 0 ? null : (
              <p>Vitals: {v.vitals.map((o) => `${o.name} ${o.value} ${o.unit}`).join('; ')}</p>
            )}
            {v.notes.map((n, i) => (
              <div key={`${v.id}-note-${String(i)}`} className={styles.printNote}>
                <p>
                  Note by {n.author}
                  {n.signedAt === null ? '' : `, signed ${formatInstant(n.signedAt)}`}
                  {n.amended ? ', amended' : ''}
                </p>
                {n.sections.map((s) => (
                  <p key={s.heading}>
                    {s.heading}: {s.text}
                  </p>
                ))}
              </div>
            ))}
            {v.prescriptions.length === 0 ? null : (
              <p>
                Prescribed:{' '}
                {v.prescriptions
                  .map(
                    (p) =>
                      `${p.drug} ${p.dose} ${p.route} ${p.frequency}${p.duration === null ? '' : ` for ${p.duration}`}`,
                  )
                  .join('; ')}
              </p>
            )}
            {v.labResults.map((l, i) => (
              <p key={`${v.id}-lab-${String(i)}`}>
                {l.test}:{' '}
                {l.results
                  .map(
                    (x) =>
                      `${x.name} ${x.value}${x.unit === null ? '' : ` ${x.unit}`}${x.flag === null ? '' : ` (${x.flag})`}`,
                  )
                  .join('; ')}
              </p>
            ))}
          </div>
        ))}
        {data.truncated ? <p>Only the most recent visits are included; ask for the rest.</p> : null}
      </section>
      <section className={styles.printSection}>
        <h2>Documents</h2>
        <p>
          {`${String(r.documents.sickSheets)} sick sheets, ${String(r.documents.referralLetters)} referral letters and ${String(r.documents.uploads)} uploaded documents are held. Ask the medical records office for copies.`}
        </p>
      </section>
      <PrintFooter>{data.notice}</PrintFooter>
    </PrintSheet>
  );
}
